import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { FormGroup, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { Buttons } from 'src/app/enums/buttons.enum';
import { IResult, IResultModel, Result } from 'src/app/models/general/result.model';
import { Position, PositionModel } from 'src/app/models/position.model';
import { State } from 'src/app/models/state.model';
import { PositionService, StateService } from 'src/app/services';
import { SearchConfig, SearchOption } from 'src/app/shared/models';
import { ErrorMessageService } from 'src/app/shared/services';
import { AppState } from 'src/app/store/app.state';

import { ConfirmDialogComponent, IConfirmDialogModel } from 'src/app/shared/components/dialogs';

@UntilDestroy()
@Component({
  selector: 'app-position',
  templateUrl: './position.component.html',
  styleUrls: ['./position.component.scss']
})
export class PositionComponent implements OnInit {
  
  position: Position;
  public mainForm: UntypedFormGroup = null;

  list: Position[] = [];

  isAdd: boolean = false;
  public state: State;

  public isPositionDataExpanded = false;

  public positionProfilesValid = true;

  public searchConfig: SearchConfig<Position, number> = {
    mapItem: (model: Position) => {
      return {
        id: model.id, 
        text: model.name,
        dropText: model.name,
        groupKey: model.groupKey,
        itemSource: model 
      } as SearchOption<Position, number>;
    },
    getList: this.positionService.GetListForCommonAndCurrentCompany
  };

  public get hasId(): boolean {
    return !!this.position?.id;
  }

  public get hasPermissionToModify(): boolean {
    return this.state.isSuperAdmin || this.state.isAdmin;
  }

  public get isAllowEdit(): boolean {
    return this.position?.companyId === this.appState.company?.value?.id;
  }

  public get isAllowAdd(): boolean {
    return !this.isAdd;
  }

  public get isAllowDelete(): boolean {
    return this.hasId;
  }

  public get isAllowCopy(): boolean {
    return this.hasId;
  }

  public get isAllowSave(): boolean {

    if (this.isAdd)
      return this.mainForm.valid;
    else
    {
      const res = this.mainForm.valid && this.positionProfilesValid;
      //console.log(`mainform.valid: ${this.mainForm.valid}, mainform.dirty: ${this.mainForm.dirty}, positionProfilesValid: ${this.positionProfilesValid}, res: ${res}`);
      return res;
    }
  }

  constructor(
    private route: ActivatedRoute,
    private fb: UntypedFormBuilder,
    private router: Router,
    private msgService: ErrorMessageService,
    public appState: AppState,
    private stateService: StateService,
    private positionService: PositionService,
    private dialog: MatDialog,
    private translate: TranslateService,
  ) {
    this.state = this.stateService.state;

    this.mainForm = this.createEditForm();
  }

  ngOnInit(): void {

    this.route.params
      .pipe(untilDestroyed(this))
      .subscribe(params => {
        let positionId = params['positionId'];
        this.isAdd = this.router.url.includes('add');

        this.loadData(positionId);
    });

  }

  private createEditForm(): UntypedFormGroup {
    return this.fb.group({
      'id': 0,
      'name': ['', [Validators.required]],
      'description': null,
      'profilesDescription' : null,
      'groupKey': null,
    });
  }

  public loadData(positionId?: number) {
    if (positionId) {
      this.positionService.getItem(positionId).subscribe({
        next: (result) => {
          this.position = result;
          this.loadForm();
        }
      });
    }
    else if (this.isAdd)
    {
      let p = new PositionModel();
      p.groupKey = this.getGroupKey();
      p.companyId = this.appState.company?.value?.id;
      this.position = p;
      this.loadForm();
      this.isPositionDataExpanded = true;
    }
    else
    {
      this.positionService.getFirst().subscribe({
        next: (result) => {
          this.position = result;
          this.loadForm();
        }
      });
    }

  }

  getGroupKey() {
    return this.appState.company?.value?.id === 1 ? 'Cégfüggetlen munkakörök' : 'Cégen belüli munkakörök';
  }

  loadForm() {
    this.mainForm.patchValue(this.position);
  }

  onOptionClick(option: SearchOption<Position, number>) {    
      if (!!(option as any).type) return;
  
      this.router.navigate([`/position/${option.id}`]);
  }

  ok(event: any) {
    this.saveChanges(Buttons.Ok);
  }

  save(event: any) {
    this.saveChanges(Buttons.Save);
  }

  copy(event: any) {
    this.positionService.copy(this.position.id).subscribe({
      next: (result) => {
        this.msgService.showSnackBarMessage('copySuccessful');
        this.router.navigate([`/position/${result.data}`]);
      },
      error: (err: IResult<number, Result<number>>) => {
        this.msgService.handleErrorWithData<number, Result<number>>(err);
      }
    });
  }

  delete(event: any) {

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      maxWidth: "600px",
      data: {
          title: this.translate.instant('POSITION.DELETE_POSITION_QUESTION'),
          confirmButtonConfig: {name: this.translate.instant('POSITION.DELETE'), color: 'warn'},
      } as IConfirmDialogModel
    });

    dialogRef.afterClosed().subscribe( (dialogResult: boolean) => {
      if (dialogResult)
      {
  
        this.positionService.deleteItem(this.position).subscribe({
          next: (result) => {
            this.msgService.showSnackBarMessage('deleteSuccessful');
            this.router.navigate(['/position']);
          },
          error: (err: IResult<Position, Result<Position>>) => {
            this.msgService.handleErrorWithData<Position, Result<Position>>(err);
          }
        });

      }
    });
  }

  onProfilesValidityChange(isValid: boolean) {
    this.positionProfilesValid = isValid;
  }

  saveChanges(bt: Buttons) {
    let model = this.mainForm.value;
    this.position.name = model.name;
    this.position.description = model.description;
    this.position.profilesDescription = model.profilesDescription;

    let action = this.isAdd ? this.positionService.addItem<Position> : this.positionService.updateItem<Position>;

    action.bind(this.positionService)(this.position).subscribe({
      next: (result) => {

        this.msgService.showSnackBarMessage('saveSuccessful');

        if (this.isAdd) {
          this.position.id = result.data.id;
          this.router.navigate([`/position/${result.data.id}`]);
          return;
        }

        if (bt === Buttons.Ok) {
          this.router.navigate(['/settings']);
          return;
        }

      },
      error: (err: IResult<Position, Result<Position>>) => {
        this.msgService.handleErrorWithData<Position, Result<Position>>(err);
      }
    });

  }

  selectPosition(event: any) {
  
  }
}
